/**
 * Parse a user-typed date string into a Date at local midnight.
 *
 * Supported formats:
 * - YYYY-MM-DD (ISO)
 * - YYYY/MM/DD (universal)
 * - MM/DD/YYYY (US)
 * - DD/MM/YYYY (EU — disambiguated: first number > 12 means DD/MM)
 * - M/D/YYYY or M/D/YY (short US)
 * - YYYYMMDD (compact)
 *
 * @returns Date at local midnight, or null if unparseable/invalid
 */
export function parseDateInput(input: string): Date | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  let year: number | null = null;
  let month: number | null = null;
  let day: number | null = null;

  // YYYY-MM-DD (ISO)
  const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    year = parseInt(isoMatch[1], 10);
    month = parseInt(isoMatch[2], 10);
    day = parseInt(isoMatch[3], 10);
  }

  // YYYYMMDD (compact) — 8 digits, no separators
  if (year === null) {
    const compactMatch = trimmed.match(/^(\d{4})(\d{2})(\d{2})$/);
    if (compactMatch) {
      year = parseInt(compactMatch[1], 10);
      month = parseInt(compactMatch[2], 10);
      day = parseInt(compactMatch[3], 10);
    }
  }

  // YYYY/MM/DD (universal)
  if (year === null) {
    const yearFirstMatch = trimmed.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
    if (yearFirstMatch) {
      year = parseInt(yearFirstMatch[1], 10);
      month = parseInt(yearFirstMatch[2], 10);
      day = parseInt(yearFirstMatch[3], 10);
    }
  }

  // Slash-separated: M/D/YYYY, M/D/YY, MM/DD/YYYY, DD/MM/YYYY
  if (year === null) {
    const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
    if (slashMatch) {
      const first = parseInt(slashMatch[1], 10);
      const second = parseInt(slashMatch[2], 10);
      let yearPart = parseInt(slashMatch[3], 10);

      // Expand 2-digit year
      if (yearPart < 100) {
        yearPart += yearPart < 50 ? 2000 : 1900;
      }

      // Disambiguate: if first > 12, it must be DD/MM/YYYY (EU)
      if (first > 12) {
        day = first;
        month = second;
      } else {
        // Treat as MM/DD/YYYY (US default)
        month = first;
        day = second;
      }
      year = yearPart;
    }
  }

  if (year === null || month === null || day === null) {
    return null;
  }

  // Validate ranges
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;

  // Create date and validate it's real (catches Feb 30, etc.)
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  return date;
}
